import { AGENT_FUNNEL_PERIOD_PRESETS, AGENT_QUANTITY_STAGES, AgentFunnelComparePeriod, AgentFunnelDataSource, AgentFunnelYMetric, } from '@/entities/funnel-heatmap';
import { AGENT_SCOPE_QUERY } from '@/shared/model/agent-scope';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
export const DEFAULT_QUALITY_MONTHS = 6;
export const DEFAULT_QUANTITY_MONTHS = 3;
export const DEFAULT_Y_METRIC = AgentFunnelYMetric.OBJECT_VISITS;
export const DEFAULT_COMPARE_PERIOD = AgentFunnelComparePeriod.NONE;
export const DEFAULT_DATA_SOURCE = AgentFunnelDataSource.AGENTS;
const PERIOD_PRESET_SET = new Set(AGENT_FUNNEL_PERIOD_PRESETS);
const Y_METRIC_VALUES = new Set(Object.values(AgentFunnelYMetric));
const COMPARE_PERIOD_VALUES = new Set(Object.values(AgentFunnelComparePeriod));
const DATA_SOURCE_VALUES = new Set(Object.values(AgentFunnelDataSource));
export const parseQualityMonths = (value) => {
    const n = Number(value);
    if (Number.isInteger(n) && PERIOD_PRESET_SET.has(n))
        return n;
    return DEFAULT_QUALITY_MONTHS;
};
export const parseQuantityMonths = (value) => {
    const n = Number(value);
    if (Number.isInteger(n) && PERIOD_PRESET_SET.has(n))
        return n;
    return DEFAULT_QUANTITY_MONTHS;
};
export const parseYMetric = (value) => {
    if (value != null && Y_METRIC_VALUES.has(value)) {
        return value;
    }
    return DEFAULT_Y_METRIC;
};
export const parseComparePeriod = (value) => {
    if (value != null && COMPARE_PERIOD_VALUES.has(value)) {
        return value;
    }
    return DEFAULT_COMPARE_PERIOD;
};
export const parseDataSource = (value) => {
    if (value != null && DATA_SOURCE_VALUES.has(value)) {
        return value;
    }
    return DEFAULT_DATA_SOURCE;
};
export const dataSourceToSelectedValues = (source) => {
    if (source === AgentFunnelDataSource.BOTH) {
        return [AgentFunnelDataSource.AGENTS, AgentFunnelDataSource.CRM];
    }
    return [source];
};
export const selectedValuesToDataSource = (values) => {
    const selected = new Set(values.filter((value) => value === AgentFunnelDataSource.AGENTS ||
        value === AgentFunnelDataSource.CRM));
    const hasAgents = selected.has(AgentFunnelDataSource.AGENTS);
    const hasCrm = selected.has(AgentFunnelDataSource.CRM);
    if (hasAgents && hasCrm)
        return AgentFunnelDataSource.BOTH;
    if (hasCrm)
        return AgentFunnelDataSource.CRM;
    return DEFAULT_DATA_SOURCE;
};
export const parseProjectId = (value) => {
    if (value == null || value.trim() === '')
        return 'all';
    const n = Number(value);
    if (Number.isInteger(n) && n > 0)
        return n;
    return 'all';
};
export const parseAgencyNames = (values) => {
    const seen = new Set();
    const result = [];
    for (const value of values) {
        const trimmed = value.trim().replace(/\s+/g, ' ');
        if (!trimmed)
            continue;
        const key = trimmed.toLocaleLowerCase('ru');
        if (seen.has(key))
            continue;
        seen.add(key);
        result.push(trimmed);
    }
    return result;
};
export const parseActiveAgentsOnly = (value) => value === 'true';
export const parseDealSources = (values) => parseAgencyNames(values);
const MAX_STAGE_ORDER = AGENT_QUANTITY_STAGES.length - 1;
const TRANSITION_KEY_PATTERN = /^\d+:\d+->\d+:\d+$/;
const parseNonNegativeInt = (raw) => {
    if (!/^\d+$/.test(raw))
        return null;
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 0)
        return null;
    return n;
};
const parseStageOrder = (raw) => {
    const n = parseNonNegativeInt(raw);
    if (n == null || n > MAX_STAGE_ORDER)
        return null;
    return n;
};
export const parseHeatmapCell = (value) => {
    if (value == null || value.trim() === '')
        return null;
    const trimmed = value.trim();
    const transitionMatch = /^tr:(.+)$/.exec(trimmed);
    if (transitionMatch) {
        const transitionKey = transitionMatch[1] ?? '';
        if (!TRANSITION_KEY_PATTERN.test(transitionKey))
            return null;
        return { kind: 'transition', transitionKey };
    }
    const twoPart = /^(cd|c):(\d+):(\d+)$/.exec(trimmed);
    if (twoPart) {
        const quantityStageOrder = parseStageOrder(twoPart[2] ?? '');
        const yBucket = parseNonNegativeInt(twoPart[3] ?? '');
        if (quantityStageOrder == null || yBucket == null)
            return null;
        return {
            kind: twoPart[1] === 'cd' ? 'cellDelta' : 'cell',
            quantityStageOrder,
            yBucket,
        };
    }
    const onePart = /^(td|t):(\d+)$/.exec(trimmed);
    if (onePart) {
        const quantityStageOrder = parseStageOrder(onePart[2] ?? '');
        if (quantityStageOrder == null)
            return null;
        return {
            kind: onePart[1] === 'td' ? 'totalDelta' : 'total',
            quantityStageOrder,
        };
    }
    return null;
};
export const serializeHeatmapCell = (selection) => {
    if (selection == null)
        return null;
    switch (selection.kind) {
        case 'cell':
            return `c:${selection.quantityStageOrder}:${selection.yBucket}`;
        case 'cellDelta':
            return `cd:${selection.quantityStageOrder}:${selection.yBucket}`;
        case 'total':
            return `t:${selection.quantityStageOrder}`;
        case 'totalDelta':
            return `td:${selection.quantityStageOrder}`;
        case 'transition':
            return `tr:${selection.transitionKey}`;
    }
};
export const parseFocusAgent = (value) => {
    if (value == null)
        return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
};
const qualityMonthsToQuery = (months) => months === DEFAULT_QUALITY_MONTHS ? null : String(months);
const quantityMonthsToQuery = (months) => months === DEFAULT_QUANTITY_MONTHS ? null : String(months);
const yMetricToQuery = (metric) => metric === DEFAULT_Y_METRIC ? null : metric;
const comparePeriodToQuery = (period) => (period === DEFAULT_COMPARE_PERIOD ? null : period);
const dataSourceToQuery = (source) => source === DEFAULT_DATA_SOURCE ? null : source;
const projectIdToQuery = (projectId) => projectId === 'all' ? null : String(projectId);
const agencyNamesToQuery = (agencyNames) => agencyNames.length > 0 ? agencyNames : null;
const activeAgentsOnlyToQuery = (activeAgentsOnly) => activeAgentsOnly ? 'true' : null;
const dealSourcesToQuery = (dealSources) => dealSources.length > 0 ? dealSources : null;
export const isFunnelFiltersDefault = (filters) => filters.qualityMonths === DEFAULT_QUALITY_MONTHS &&
    filters.quantityMonths === DEFAULT_QUANTITY_MONTHS &&
    filters.yMetric === DEFAULT_Y_METRIC &&
    filters.comparePeriod === DEFAULT_COMPARE_PERIOD &&
    filters.dataSource === DEFAULT_DATA_SOURCE &&
    filters.projectId === 'all' &&
    filters.agencyNames.length === 0 &&
    !filters.activeAgentsOnly &&
    filters.dealSources.length === 0;
/** Stable list key — ignore cell/focusAgent and other non-report URL noise. */
function searchParamListKey(searchParams, key) {
    return searchParams.getAll(key).join('\u0001');
}
export function useFunnelFilters() {
    const [searchParams, setSearchParams] = useSearchParams();
    const qualityMonths = parseQualityMonths(searchParams.get('qualityMonths'));
    const quantityMonths = parseQuantityMonths(searchParams.get('quantityMonths'));
    const yMetric = parseYMetric(searchParams.get('yMetric'));
    const comparePeriod = parseComparePeriod(searchParams.get('comparePeriod'));
    const dataSource = parseDataSource(searchParams.get('dataSource'));
    const projectId = parseProjectId(searchParams.get('projectId'));
    const agencyNamesKey = searchParamListKey(searchParams, 'agencyNames');
    const dealSourcesKey = searchParamListKey(searchParams, 'dealSources');
    const agencyNames = useMemo(() => parseAgencyNames(agencyNamesKey ? agencyNamesKey.split('\u0001') : []), [agencyNamesKey]);
    const activeAgentsOnly = parseActiveAgentsOnly(searchParams.get('activeAgentsOnly'));
    const dealSources = useMemo(() => parseDealSources(dealSourcesKey ? dealSourcesKey.split('\u0001') : []), [dealSourcesKey]);
    const cellParam = searchParams.get('cell');
    const selectedCell = useMemo(() => parseHeatmapCell(cellParam), [cellParam]);
    const focusAgent = parseFocusAgent(searchParams.get('focusAgent'));
    const dataSourceSelection = useMemo(() => dataSourceToSelectedValues(dataSource), [dataSource]);
    /**
     * Report filters only. cell / focusAgent must not appear here — otherwise
     * every selection rewrite recreates queryParams and refetches the funnel.
     */
    const queryParams = useMemo(() => ({
        qualityMonths,
        quantityMonths,
        yMetric,
        comparePeriod,
        dataSource: dataSource === DEFAULT_DATA_SOURCE ? undefined : dataSource,
        projectId: projectId === 'all' ? undefined : projectId,
        agencyNames: agencyNames.length > 0 ? agencyNames : undefined,
        activeAgentsOnly: activeAgentsOnly || undefined,
        dealSources: yMetric === AgentFunnelYMetric.UNITY_SOURCES && dealSources.length > 0
            ? dealSources
            : undefined,
    }), [
        qualityMonths,
        quantityMonths,
        yMetric,
        comparePeriod,
        dataSource,
        projectId,
        agencyNames,
        activeAgentsOnly,
        dealSources,
    ]);
    /** String identity for load effects — equal when report inputs are equal. */
    const reportQueryKey = useMemo(() => [
        qualityMonths,
        quantityMonths,
        yMetric,
        comparePeriod,
        dataSource,
        projectId === 'all' ? '' : String(projectId),
        agencyNamesKey,
        activeAgentsOnly ? '1' : '',
        yMetric === AgentFunnelYMetric.UNITY_SOURCES ? dealSourcesKey : '',
    ].join('\u0000'), [
        qualityMonths,
        quantityMonths,
        yMetric,
        comparePeriod,
        dataSource,
        projectId,
        agencyNamesKey,
        activeAgentsOnly,
        dealSourcesKey,
    ]);
    const updateParams = useCallback((updates) => {
        const params = new URLSearchParams(searchParams.toString());
        for (const [key, value] of Object.entries(updates)) {
            params.delete(key);
            if (value === null)
                continue;
            if (Array.isArray(value)) {
                for (const item of value) {
                    params.append(key, item);
                }
            }
            else {
                params.set(key, value);
            }
        }
        const next = params.toString();
        if (next === searchParams.toString())
            return;
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);
    const updateParamsClearingSelection = useCallback((updates) => {
        updateParams({ ...updates, cell: null, focusAgent: null });
    }, [updateParams]);
    const setQualityMonths = useCallback((months) => {
        updateParamsClearingSelection({
            qualityMonths: qualityMonthsToQuery(months),
        });
    }, [updateParamsClearingSelection]);
    const setQuantityMonths = useCallback((months) => {
        updateParamsClearingSelection({
            quantityMonths: quantityMonthsToQuery(months),
        });
    }, [updateParamsClearingSelection]);
    const setYMetric = useCallback((metric) => {
        updateParamsClearingSelection({ yMetric: yMetricToQuery(metric) });
    }, [updateParamsClearingSelection]);
    const setComparePeriod = useCallback((period) => {
        updateParamsClearingSelection({
            comparePeriod: comparePeriodToQuery(period),
        });
    }, [updateParamsClearingSelection]);
    const setDataSource = useCallback((source) => {
        updateParamsClearingSelection({ dataSource: dataSourceToQuery(source) });
    }, [updateParamsClearingSelection]);
    const setDataSourceSelection = useCallback((values) => {
        setDataSource(selectedValuesToDataSource(values));
    }, [setDataSource]);
    const setProjectId = useCallback((id) => {
        updateParamsClearingSelection({ projectId: projectIdToQuery(id) });
    }, [updateParamsClearingSelection]);
    const setAgencyNames = useCallback((names) => {
        updateParamsClearingSelection({ agencyNames: agencyNamesToQuery(names) });
    }, [updateParamsClearingSelection]);
    const setActiveAgentsOnly = useCallback((enabled) => {
        updateParamsClearingSelection({
            activeAgentsOnly: activeAgentsOnlyToQuery(enabled),
        });
    }, [updateParamsClearingSelection]);
    const setDealSources = useCallback((sources) => {
        updateParamsClearingSelection({
            dealSources: dealSourcesToQuery(sources),
        });
    }, [updateParamsClearingSelection]);
    const setSelectedCell = useCallback((selection) => {
        updateParams({
            cell: serializeHeatmapCell(selection),
            focusAgent: null,
        });
    }, [updateParams]);
    const setFocusAgent = useCallback((key) => {
        updateParams({
            focusAgent: key == null || key.trim() === '' ? null : key,
        });
    }, [updateParams]);
    const selectAgentInCell = useCallback((selection, agentKey) => {
        updateParams({
            cell: serializeHeatmapCell(selection),
            focusAgent: agentKey.trim() === '' ? null : agentKey,
        });
    }, [updateParams]);
    const clearSelection = useCallback(() => {
        updateParams({ cell: null, focusAgent: null });
    }, [updateParams]);
    const resetFilters = useCallback(() => {
        const params = new URLSearchParams();
        const agentScope = searchParams.get(AGENT_SCOPE_QUERY);
        if (agentScope)
            params.set(AGENT_SCOPE_QUERY, agentScope);
        setSearchParams(params, { replace: true });
    }, [searchParams, setSearchParams]);
    const isDefaultFilters = isFunnelFiltersDefault({
        qualityMonths,
        quantityMonths,
        yMetric,
        comparePeriod,
        dataSource,
        projectId,
        agencyNames,
        activeAgentsOnly,
        dealSources,
    });
    return {
        qualityMonths,
        quantityMonths,
        yMetric,
        comparePeriod,
        dataSource,
        dataSourceSelection,
        projectId,
        agencyNames,
        activeAgentsOnly,
        dealSources,
        selectedCell,
        focusAgent,
        queryParams,
        reportQueryKey,
        isDefaultFilters,
        setQualityMonths,
        setQuantityMonths,
        setYMetric,
        setComparePeriod,
        setDataSource,
        setDataSourceSelection,
        setProjectId,
        setAgencyNames,
        setActiveAgentsOnly,
        setDealSources,
        setSelectedCell,
        setFocusAgent,
        selectAgentInCell,
        clearSelection,
        resetFilters,
    };
}
